/**
 * The notification contract, in one place.
 *
 * Two kinds of notification row exist in this product:
 *
 *  1. Trigger-created. public.enqueue_assignment_notifications() writes the
 *     assignment confirmation and the T-48h / T-24h reminders whenever an
 *     assignment is created. The demo store has to mirror that by hand,
 *     otherwise assigning a coach in demo mode would leave their inbox empty
 *     while the real backend fills it.
 *
 *  2. App-created. Nothing in SQL enqueues an availability request or tells a
 *     coach their qualification was approved, so src/lib/api.ts writes those
 *     rows itself - in BOTH the demo and the Supabase path, from the builders
 *     below, so the two backends produce byte-identical copy and dedupe keys.
 *
 * That is why this module lives under mock/ but is imported by api.ts: it is
 * the single definition of the contract, not demo-only data.
 *
 * Copy rule: no em dashes in anything a coach reads. Plain hyphens.
 */

import type {
  Assignment,
  EventRow,
  NotificationPayload,
  NotificationQueueRow,
  NotificationType,
  Service,
} from '../../types/db'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * The exact string public.format_dubai() writes into notification payloads:
 * to_char(ts at time zone 'Asia/Dubai', 'DD Mon YYYY "at" HH24:MI').
 *
 * Done by arithmetic rather than Intl because Asia/Dubai is UTC+4 with no DST,
 * which is the same assumption the SQL makes.
 */
export function formatDubaiStamp(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 4 * 3_600_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} at ${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())}`
}

/** A row the caller is about to insert. id / created_at are filled by the store. */
export type NewNotification = Omit<NotificationQueueRow, 'id' | 'created_at'>

interface BuildArgs {
  coachId: string
  type: NotificationType
  event: EventRow | null
  assignmentId: string | null
  serviceCode: Service['code'] | null
  title: string
  body: string
  url: string
  sendAt: string
  dedupeKey: string
}

function build(args: BuildArgs): NewNotification {
  const payload: NotificationPayload = {
    title: args.title,
    body: args.body,
    url: args.url,
    event_id: args.event?.id ?? null,
    service_code: args.serviceCode,
    starts_at_local: args.event ? formatDubaiStamp(args.event.starts_at) : null,
  }
  return {
    coach_id: args.coachId,
    type: args.type,
    channel: 'push',
    event_id: args.event?.id ?? null,
    assignment_id: args.assignmentId,
    payload,
    send_at: args.sendAt,
    status: 'pending',
    attempts: 0,
    sent_at: null,
    last_error: null,
    acknowledged_at: null,
    dedupe_key: args.dedupeKey,
  }
}

/* -------------------------------------------------------------------------- */
/* 1. Trigger mirrors                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Mirror of public.enqueue_assignment_notifications().
 *
 * `at` is the moment the enqueue runs (now() in SQL). Reminders whose send_at
 * has already passed are skipped, exactly as the SQL does, so a late assignment
 * never fires a stale "in 48 hours" push.
 */
export function buildAssignmentNotifications(
  assignment: Assignment,
  event: EventRow,
  service: Service | undefined,
  at: string,
): NewNotification[] {
  if (assignment.status === 'cancelled') return []
  if (event.cancelled_at !== null || event.status === 'cancelled') return []

  const when = formatDubaiStamp(event.starts_at)
  const where = event.location_name ?? 'location to be confirmed'
  const serviceCode = service?.code ?? null
  const url = `/events/${event.id}`
  const rows: NewNotification[] = [
    build({
      coachId: assignment.coach_id,
      type: 'assignment_created',
      event,
      assignmentId: assignment.id,
      serviceCode,
      title: `You are on ${service?.name ?? 'an event'}`,
      body: `${event.title} at ${where} on ${when}. Tap for the brief.`,
      url,
      sendAt: at,
      dedupeKey: `assignment:${assignment.id}:assignment_created`,
    }),
  ]

  const reminders: Array<[number, NotificationType, string]> = [
    [48, 'event_reminder_48h', 'Event in 48 hours'],
    [24, 'event_reminder_24h', 'Event in 24 hours'],
  ]
  const startsAt = new Date(event.starts_at).getTime()
  const atMs = new Date(at).getTime()
  for (const [hoursBefore, type, title] of reminders) {
    const sendAt = startsAt - hoursBefore * 3_600_000
    if (sendAt <= atMs) continue
    rows.push(
      build({
        coachId: assignment.coach_id,
        type,
        event,
        assignmentId: assignment.id,
        serviceCode,
        title,
        body: `${service?.name ?? 'Event'} at ${where} on ${when}. Tap to confirm you have seen this.`,
        url,
        sendAt: new Date(sendAt).toISOString(),
        dedupeKey: `assignment:${assignment.id}:${type}`,
      }),
    )
  }
  return rows
}

/** Mirror of the event_cancelled branch of public.events_after_update(). */
export function buildEventCancelledNotification(
  assignment: Assignment,
  event: EventRow,
  at: string,
): NewNotification {
  return build({
    coachId: assignment.coach_id,
    type: 'event_cancelled',
    event,
    assignmentId: assignment.id,
    serviceCode: null,
    title: 'Event cancelled',
    body: `${event.title} on ${formatDubaiStamp(event.starts_at)} has been cancelled. No action needed.`,
    url: `/events/${event.id}`,
    sendAt: at,
    dedupeKey: `assignment:${assignment.id}:event_cancelled`,
  })
}

/* -------------------------------------------------------------------------- */
/* 2. App-created rows                                                        */
/* -------------------------------------------------------------------------- */

/**
 * "Can you work this?" Sent by the portal to the coaches who are qualified for
 * the service, which is the step that replaces Michel messaging people one by
 * one on WhatsApp.
 */
export function buildAvailabilityRequestNotification(
  coachId: string,
  event: EventRow,
  service: Service | undefined,
  at: string,
): NewNotification {
  const where = event.location_name ?? 'location to be confirmed'
  return build({
    coachId,
    type: 'availability_request',
    event,
    assignmentId: null,
    serviceCode: service?.code ?? null,
    title: `Are you free for ${event.title}?`,
    body: `${where} on ${formatDubaiStamp(event.starts_at)}. Tap to answer yes, no or maybe.`,
    url: `/events/${event.id}`,
    sendAt: at,
    dedupeKey: `availability:${event.id}:${coachId}:availability_request`,
  })
}

/** Michel approved the qualification, so a new service just opened up. */
/**
 * A decision that turns a coach away, with the reason they were promised.
 * There is no 'qualification_revoked' value in public.notification_type, so
 * this goes out as an announcement: the title and body carry the meaning.
 * Fair tone: the reason, then what they can do next. Never a bare "no".
 */
export function buildQualificationDecisionNotification(
  coachId: string,
  qualificationId: string,
  service: Service,
  reason: string,
  wasApproved: boolean,
  at: string,
): NewNotification {
  const title = wasApproved
    ? `${service.name}: approval withdrawn`
    : `${service.name}: not approved yet`
  const next = wasApproved
    ? 'Events for this service leave your schedule for now. Talk to Michel about what would bring it back.'
    : 'You can ask again once this is sorted.'
  return build({
    coachId,
    type: 'announcement',
    event: null,
    assignmentId: null,
    serviceCode: service.code,
    title,
    body: `Reason given: ${reason} ${next}`,
    url: '/training',
    sendAt: at,
    // One row per decision: a later re-approval and second withdrawal is news
    // again, so the timestamp is part of the key.
    dedupeKey: `qualification:${qualificationId}:decision:${at}`,
  })
}

export function buildQualificationApprovedNotification(
  coachId: string,
  qualificationId: string,
  service: Service,
  at: string,
): NewNotification {
  return build({
    coachId,
    type: 'qualification_approved',
    event: null,
    assignmentId: null,
    serviceCode: service.code,
    title: `${service.name} unlocked`,
    body: `You are now qualified for ${service.name}. These events will start appearing in your schedule.`,
    url: '/training',
    sendAt: at,
    dedupeKey: `qualification:${qualificationId}:qualification_approved`,
  })
}
